

import { useDropdown } from "@/components/DropDown/hooks/useDropdown";
import { lastPressWasTouch } from "@/lib/utils/pointer";
import { addOutsidePressListener } from "@/lib/slotting/outsidePress";
import { IconChevronDown, IconLoader2 } from "@tabler/icons-react";
import { createPortal } from "react-dom";
import {
  ReactNode,
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  useState,
} from "react";

export type DropDownRef = {
  width: number;
};

type DropDownProps = {
  margin?: string;
  loading?: boolean;
  changeDefault?: boolean;
  border?: boolean;
  borderColor?: string;
  dialogTop?: boolean;
  translate?: boolean;
  translationSelected?: (
    key: string,
    values?: Record<string, string>
  ) => string;
  translation?: (key: string, values?: Record<string, string>) => string;
  initialItem: string;
  allItems: string[];
  /**
   * Tailwind max-width class (e.g. "max-w-40") for the trigger label. When set,
   * the selected item is kept to one line and truncated with an ellipsis
   * instead of wrapping. Off by default so existing dropdowns are unaffected.
   */
  selectedMaxWidth?: string;
  onDropdownAction: (item: string) => void;
  onHoverAction?: (item: string) => void;
  fontFocus?: string;
  fontFocusColor?: string;
  fontDropdown?: string;
  fontDropdownColor?: string;
  entryDesign?: (item: string) => ReactNode;
  height?: string;
  /**
   * Trigger padding. Defaults to the historical `pl-3 pr-2 py-1`; override it
   * when the trigger has to line up with a button component (SecondaryButton
   * runs on py-0.75, so an unpadded default sits 2px taller than its neighbour).
   */
  padding?: string;
  disabled?: boolean;
  sort?: boolean;
  customAdditionDropdown?: React.ReactNode;
  testId?: string;
  itemTestId?: (item: string) => string | undefined;
  /**
   * While set to an item name, that item's `<li>` shows a spinner and the
   * dropdown refuses to close (mouseLeave is suppressed) so the user can see
   * the loading state. Used to surface lazy-import chunk downloads triggered
   * from dropdown actions.
   */
  loadingItem?: string | null;
};

export const DropDown = forwardRef<DropDownRef, DropDownProps>(
  (
    {
      margin,
      loading = false,
      changeDefault = true,
      border = false,
      dialogTop = false,
      translation: t,
      translationSelected: t2,
      translate = false,
      initialItem,
      allItems,
      selectedMaxWidth,
      customAdditionDropdown,
      onDropdownAction,
      onHoverAction,
      borderColor = "outline-tertiary",
      fontFocus = "body-2-medium",
      fontFocusColor = "font-tertiary",
      fontDropdown = "body-2-regular",
      fontDropdownColor = "",
      entryDesign,
      height,
      padding = "pl-3 pr-2 py-1",
      disabled = false,
      sort = false,
      testId,
      itemTestId,
      loadingItem = null,
    },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    /**
     * The menu is portaled to <body>, so it is NOT a DOM descendant of the
     * trigger any more. Every containment probe therefore has to ask both
     * nodes — outside-press, focus-out, and the roving-arrow option lookup all
     * used to rely on the menu being nested.
     */
    const ownsNode = (node: unknown): boolean => {
      // `relatedTarget` is not always an Element — it can be null, or a
      // non-Node object (jsdom hands one over for a synthesized pointerleave),
      // and Node.contains() throws on those.
      if (!(node instanceof Node)) return false;
      return (
        !!containerRef.current?.contains(node) || !!menuRef.current?.contains(node)
      );
    };

    /** Viewport coordinates of the portaled menu, clamped to stay on screen. */
    const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
    /**
     * Applied BEFORE the menu is measured. It comes from the trigger, not the
     * menu, so it is known without measuring — and setting it afterwards meant
     * the menu grew past the clamp that had just been computed for its narrower
     * natural width, with no second pass to correct it.
     */
    const [menuMinWidth, setMenuMinWidth] = useState<number | undefined>(undefined);
    const [width, setWidth] = useState(0);
    // Right-align the menu when its natural (w-max) width would spill past
    // the right viewport edge — happens on narrow screens with a trigger
    // toward the right (e.g. pagination page-size). Re-measured per open.

    const {
      isDropdownOpen,
      selectedItem,
      items,
      setIsDropdownOpen,
      handleSelect,
    } = useDropdown({
      initialItem,
      allItems,
      onDropdownAction,
      sort,
    });

    // Expose width to parent via ref
    useImperativeHandle(ref, () => ({
      width,
    }));

    /**
     * Place the portaled menu. Measured AFTER it painted at its natural width,
     * then clamped on both axes.
     *
     * The old model was `absolute left-0` with a right-flip that only fired
     * when right-anchoring happened to help; anything else overflowed, and
     * since `html` carries `overflow-x: hidden` the overhang was clipped away
     * rather than scrollable. It was also clipped vertically by every
     * `overflow-y-auto` ancestor (page-root, the guestlist root, the table),
     * which is what the portal fixes.
     */
    const MENU_MARGIN = 8;
    const placeMenu = useCallback(() => {
      const menu = menuRef.current;
      const trigger = containerRef.current;
      if (!menu || !trigger) return;
      const m = menu.getBoundingClientRect();
      const t = trigger.getBoundingClientRect();
      // Layout viewport — the menu is `position: fixed`.
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      const spaceBelow = vh - t.bottom - MENU_MARGIN;
      const spaceAbove = t.top - MENU_MARGIN;
      const placeAbove =
        dialogTop || (m.height > spaceBelow && spaceAbove > spaceBelow);

      const top = placeAbove
        ? Math.max(MENU_MARGIN, t.top - m.height)
        : Math.min(t.bottom, Math.max(MENU_MARGIN, vh - m.height - MENU_MARGIN));
      const left = Math.min(
        Math.max(MENU_MARGIN, t.left),
        Math.max(MENU_MARGIN, vw - m.width - MENU_MARGIN),
      );

      setMenuPos((prev) =>
        prev && prev.top === top && prev.left === left ? prev : { top, left },
      );
    }, [dialogTop]);

    // Two phases on purpose: width first (known from the trigger), position
    // second (needs the menu measured at that width).
    useLayoutEffect(() => {
      if (!isDropdownOpen) {
        setMenuPos(null);
        setMenuMinWidth(undefined);
        return;
      }
      setMenuMinWidth(containerRef.current?.getBoundingClientRect().width);
    }, [isDropdownOpen]);

    useLayoutEffect(() => {
      if (!isDropdownOpen || menuMinWidth === undefined) return;
      placeMenu();
    }, [isDropdownOpen, items, menuMinWidth, placeMenu]);

    useEffect(() => {
      if (!isDropdownOpen) return;
      const onChange = () => placeMenu();
      window.addEventListener("resize", onChange);
      window.addEventListener("scroll", onChange, true);
      window.visualViewport?.addEventListener("resize", onChange);
      window.visualViewport?.addEventListener("scroll", onChange);
      return () => {
        window.removeEventListener("resize", onChange);
        window.removeEventListener("scroll", onChange, true);
        window.visualViewport?.removeEventListener("resize", onChange);
        window.visualViewport?.removeEventListener("scroll", onChange);
      };
    }, [isDropdownOpen, placeMenu]);

    // Measure width whenever the component updates
    useEffect(() => {
      if (containerRef.current) {
        const newWidth = containerRef.current.offsetWidth;
        if (newWidth !== width) {
          setWidth(newWidth);
        }
      }
    }, [width, isDropdownOpen, selectedItem, items]);

    // Hover-open, pointerType-gated (per event, hybrids included): a finger
    // tap fires pointerenter with type "touch" and must not pre-open — on
    // iOS a <button> never focuses on tap, so blur would never fire and the
    // menu stuck open with no close path.
    const handleMouseEnter = (e: React.PointerEvent) => {
      if (!disabled && e.pointerType !== "touch") {
        setIsDropdownOpen(true);
      }
    };

    // Pointer leave. While a lazy-import chunk is downloading for one of
    // the items, keep the dropdown open so the user can see the per-item
    // spinner instead of the menu disappearing.
    const handleMouseLeave = (e: React.PointerEvent) => {
      if (loadingItem || e.pointerType === "touch") return;
      // The menu is portaled, so leaving the trigger towards it is a real
      // `pointerleave` — closing there would make the menu unreachable by
      // mouse. `relatedTarget` names the node being entered, so a move
      // BETWEEN the two halves is not a leave at all. Same in reverse.
      if (ownsNode(e.relatedTarget as Node)) return;
      setIsDropdownOpen(false);
    };

    // Touch has neither mouseleave nor blur (iOS buttons don't focus on
    // tap) — close on any press outside the container (panel is nested, so
    // one ref covers trigger + list).
    useEffect(() => {
      if (!isDropdownOpen) return;
      return addOutsidePressListener((e) => {
        if (ownsNode(e.target as Node)) return;
        setIsDropdownOpen(false);
      });
    }, [isDropdownOpen]);

    // ── keyboard operability (ScreenSwitcher pattern; hover-open preserved) ──
    const triggerRef = useRef<HTMLButtonElement>(null);
    const optionButtons = (): HTMLButtonElement[] =>
      Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="option"]') ?? []);
    const focusOption = (index: number) => {
      const opts = optionButtons();
      if (opts.length) opts[Math.max(0, Math.min(index, opts.length - 1))].focus();
    };
    const closeAndRefocus = () => {
      setIsDropdownOpen(false);
      triggerRef.current?.focus();
    };
    // Keyboard users have no mouseleave — close when focus leaves the whole
    // container (suppressed while a lazy chunk is loading, like mouseleave).
    const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
      if (loadingItem) return;
      if (!ownsNode(e.relatedTarget as Node)) setIsDropdownOpen(false);
    };
    const onTriggerKeyDown = (e: React.KeyboardEvent) => {
      if (disabled) return;
      // Enter/Space TOGGLE (keyboard never has a synthetic hover-open, so
      // toggle is safe); the mouse/touch click path below stays open-only.
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setIsDropdownOpen((prev: boolean) => !prev);
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setIsDropdownOpen(true);
        requestAnimationFrame(() => focusOption(0));
      } else if (e.key === "Escape" && isDropdownOpen) {
        e.preventDefault();
        setIsDropdownOpen(false);
      }
    };
    const onListKeyDown = (e: React.KeyboardEvent) => {
      const opts = optionButtons();
      const current = opts.indexOf(document.activeElement as HTMLButtonElement);
      if (e.key === "ArrowDown") {
        e.preventDefault();
        focusOption(current + 1);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        if (current <= 0) closeAndRefocus();
        else focusOption(current - 1);
      } else if (e.key === "Home") {
        e.preventDefault();
        focusOption(0);
      } else if (e.key === "End") {
        e.preventDefault();
        focusOption(opts.length - 1);
      } else if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        closeAndRefocus();
      }
    };

    return (
      <>
        {loading ? (
          <div className="w-24 h-5 surface-grey animate-pulse rounded-md"></div>
        ) : (
          <div
            ref={containerRef}
            className={`relative ${height}`}
            data-testid={testId}
            onPointerEnter={handleMouseEnter}
            onPointerLeave={handleMouseLeave}
            onBlur={handleBlur}
          >
            {/* Trigger — a real button so keyboard users can reach it.
                Hover-driven clicks are OPEN-only (pointerenter already
                opened; a toggle would instantly re-close). A TOUCH press
                never hover-opens (pointerType gate), so there tap toggles —
                the standard mobile affordance. */}
            <button
              type="button"
              ref={triggerRef}
              disabled={disabled}
              aria-haspopup="listbox"
              aria-expanded={isDropdownOpen && !disabled}
              onClick={() =>
                setIsDropdownOpen((prev: boolean) =>
                  lastPressWasTouch() ? !prev : true,
                )
              }
              onKeyDown={onTriggerKeyDown}
              className={`inline-flex h-full flex-row justify-center items-center ${padding} text-left ${
                disabled
                  ? "opacity-50"
                  : "cursor-pointer group-hover:surface-secondary"
              } ${
                border
                  ? `border ${borderColor} ${
                      isDropdownOpen ? "outline-focus" : ""
                    } rounded-lg ${fontFocusColor}`
                  : ""
              } ${margin}`}
            >
              <p
                className={`${fontFocus} ${
                  selectedMaxWidth ? `${selectedMaxWidth} truncate` : ""
                }`}
              >
                {translate && t2 ? t2(selectedItem) : selectedItem}
              </p>
              <IconChevronDown
                className={`ml-1 transition-transform duration-300 motion-reduce:transition-none ${
                  isDropdownOpen ? "rotate-180" : ""
                }`}
                size={20}
              />
            </button>

            {/* Dropdown Menu — portaled to <body> so it escapes every
                `overflow-y-auto` ancestor (page-root, the list roots, the
                table) and every `relative z-0` stacking context. Rendered
                inline it was clipped, and the clipped part was unreachable:
                `html` carries `overflow-x: hidden`. Hidden until placed, so
                the measure pass never flashes at 0,0. */}
            {isDropdownOpen && !disabled && createPortal(
              <div
                ref={menuRef}
                style={{
                  top: menuPos ? `${menuPos.top}px` : 0,
                  left: menuPos ? `${menuPos.left}px` : 0,
                  minWidth: menuMinWidth !== undefined ? `${menuMinWidth}px` : undefined,
                  visibility: menuPos ? "visible" : "hidden",
                }}
                onPointerEnter={handleMouseEnter}
                onPointerLeave={handleMouseLeave}
                onBlur={handleBlur}
                // z-9999 matches the house's other portaled dropdowns (WBSelect,
                // DropDownSettings, FontTypeSettings). Portaled to body the menu
                // competes in the ROOT stacking context, where z-50 lost against
                // the builder appbars (z-200) and the send dialog (z-200) that host
                // its own trigger. Inline it was `absolute z-10` and always won.
                className="fixed w-max max-w-[calc(100vw-1rem)] z-9999"
              >
                <div className={`${dialogTop ? "pb-1" : "pt-1"} `}>
                  <ul
                    role="listbox"
                    onKeyDown={onListKeyDown}
                    // Safari/Firefox-macOS never focus <button>s on click, so
                    // a click here would first BLUR the focused trigger/option
                    // (relatedTarget=null) → handleBlur unmounts the menu
                    // before mouseup and the option's onClick never fires.
                    // Preventing mousedown default keeps focus where it is.
                    onMouseDown={(e) => e.preventDefault()}
                    className="surface-primary shadow-lg rounded-lg border outline-quaternary"
                  >
                    {items.map((item: string, index: number) => {
                      const isLoading = loadingItem === item;
                      const anyLoading = loadingItem !== null;
                      return (
                        <li key={index} className="m-1">
                          {/* Option = a real button: focusable for the roving
                              arrows, Enter/Space select natively. onFocus
                              mirrors onMouseEnter so keyboard users get the
                              same hover-preview side effects. */}
                          <button
                            type="button"
                            role="option"
                            aria-selected={item === selectedItem}
                            disabled={anyLoading && !isLoading}
                            data-testid={itemTestId ? itemTestId(item) : undefined}
                            className={`w-full px-3 py-1 rounded-lg flex flex-row items-center justify-between gap-2 text-left ${fontDropdown} ${fontDropdownColor} ${
                              anyLoading
                                ? "cursor-default"
                                : "hover:surface-secondary cursor-pointer"
                            } ${isLoading ? "surface-secondary" : ""} focus:outline-none focus-visible:surface-secondary`}
                            onMouseEnter={() =>
                              !anyLoading && onHoverAction && onHoverAction(item)
                            }
                            onFocus={() =>
                              !anyLoading && onHoverAction && onHoverAction(item)
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              if (anyLoading) return;
                              handleSelect(item, changeDefault);
                            }}
                          >
                            <span className="flex-1">
                              {entryDesign
                                ? entryDesign(item)
                                : translate && t
                                ? t(item)
                                : item}
                            </span>
                            {isLoading && (
                              <IconLoader2
                                size={14}
                                className="animate-spin font-tertiary shrink-0"
                              />
                            )}
                          </button>
                        </li>
                      );
                    })}
                    {customAdditionDropdown && customAdditionDropdown}
                  </ul>
                </div>
              </div>,
              document.body,
            )}
          </div>
        )}
      </>
    );
  }
);

DropDown.displayName = "DropDown";
